import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useEffect } from 'react';
import axiosInstance from 'ns_libs/nsAxios';
import API_ENDPOINTS from 'services/urls';
import { SensitivityAnalysisResponse } from '../types';

export interface SensitivityTaskStatusResponse {
    taskId: string;
    status: 'PENDING' | 'PROGRESS' | 'SUCCESS' | 'FAILURE';
    message: string;
    result?: SensitivityAnalysisResponse;
    currentStep?: number;
    error?: string;
    timedOut?: boolean;
}

export const MAX_POLLING_DURATION_MS = 5 * 60 * 1_000;
const POLLING_INTERVAL_MS = 3 * 1_000;

const fetchTaskStatus = async (
    taskId: string,
    organizationId: string,
): Promise<SensitivityTaskStatusResponse> => {
    const endpoint = API_ENDPOINTS.TASKS.TASK_STATUS(taskId);
    console.debug('[SensitivityTaskStatus] fetching task status', { taskId, endpoint });
    const response = await axiosInstance.get(endpoint, {
        params: { organizationId },
    });
    console.debug('[SensitivityTaskStatus] task status response', { response: response.data });
    return response.data;
};

interface UseSensitivityTaskStatusOptions {
    taskId?: string | null;
    organizationId: string;
    enabled?: boolean;
}

const useSensitivityTaskStatus = ({
    taskId,
    organizationId,
    enabled = true,
}: UseSensitivityTaskStatusOptions) => {
    const queryClient = useQueryClient();
    const shouldPoll = enabled && !!taskId;
    const pollingStartTimeRef = useRef<number | null>(null);

    useEffect(() => {
        if (shouldPoll) {
            pollingStartTimeRef.current = Date.now();
        }
        return () => {
            if (!shouldPoll) {
                pollingStartTimeRef.current = null;
            }
        };
    }, [shouldPoll, taskId]);

    return useQuery({
        queryKey: ['sensitivityTaskStatus', taskId, organizationId],
        queryFn: () => fetchTaskStatus(taskId!, organizationId),
        enabled: shouldPoll,
        refetchInterval: query => {
            if (!taskId) return false;

            const elapsedTimeMs = pollingStartTimeRef.current != null
                ? Date.now() - pollingStartTimeRef.current
                : null;
            const elapsedTimeSeconds = elapsedTimeMs != null
                ? Math.round(elapsedTimeMs / 1_000)
                : null;

            console.debug(
                '[SensitivityTaskStatus] refetchInterval fired',
                {
                    taskId,
                    queryState: query.state.status,
                    hasData: !!query.state.data,
                    elapsedTimeMs,
                    elapsedTimeSeconds,
                },
            );
            if (query.state.status === 'error') {
                console.debug(
                    '[SensitivityTaskStatus] stopping polling due to query error',
                    {
                        taskId,
                        elapsedTimeMs,
                        elapsedTimeSeconds,
                    },
                );
                return false;
            }

            const { data } = query.state;
            if (data?.status === 'SUCCESS' || data?.status === 'FAILURE') {
                console.debug(
                    '[SensitivityTaskStatus] stopping polling due to terminal status',
                    {
                        taskId,
                        hasData: !!data,
                        status: data?.status,
                        elapsedTimeMs,
                        elapsedTimeSeconds,
                    },
                );
                return false;
            }

            if (elapsedTimeMs != null && elapsedTimeMs > MAX_POLLING_DURATION_MS) {
                const sentinel: SensitivityTaskStatusResponse = {
                    taskId,
                    status: 'FAILURE',
                    message: 'Polling timed out',
                    error: 'Polling exceeded maximum duration',
                    timedOut: true,
                };
                queryClient.setQueryData(
                    ['sensitivityTaskStatus', taskId, organizationId],
                    sentinel,
                );
                console.warn(
                    '[SensitivityTaskStatus] polling exceeded maximum duration. Stopping.',
                    {
                        taskId,
                        elapsedTimeMs,
                        elapsedTimeSeconds,
                        maxDurationMs: MAX_POLLING_DURATION_MS,
                        maxDurationMinutes: MAX_POLLING_DURATION_MS / 60_000,
                    },
                );
                return false;
            }

            console.debug(
                '[SensitivityTaskStatus] continuing to poll',
                { taskId, intervalMs: POLLING_INTERVAL_MS },
            );
            return POLLING_INTERVAL_MS;
        },
        retry: 3,
        retryDelay: 1_000,
    });
};

export default useSensitivityTaskStatus;
